/**
 * WP-V2-TW — harmonogram Misi i Loli (czysta funkcja czasu sesji).
 * PLAN-V2 §7.1: rano Rynek, potem kawiarnia itd.
 * W9-A: waypointy wyprowadzone z TOWN_BUILDING_HOTSPOTS (nie spoza mapy 3200).
 */

import { TOWN_BUILDING_HOTSPOTS } from './hotspots.js';
import { TOWN_WALK_BAND } from './townLayout.js';

export type NpcWaypoint = {
  /** Etykieta miejsca (debug / dymek) — musi = labelPl budynku. */
  placePl: string;
  x: number;
  y: number;
};

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/**
 * Waypoint przy budynku o danym labelPl (offset w pasie chodzenia).
 */
function waypointAt(
  labelPl: string,
  offsetX = 0,
  offsetY = 28,
): NpcWaypoint {
  const h = TOWN_BUILDING_HOTSPOTS.find((b) => b.labelPl === labelPl);
  if (!h) {
    throw new Error(`npcSchedule: brak hotspota dla „${labelPl}"`);
  }
  return {
    placePl: labelPl,
    x: h.x + offsetX,
    y: clamp(h.y + offsetY, TOWN_WALK_BAND.minY, TOWN_WALK_BAND.maxY),
  };
}

/** Cykl miejsc Misi (maltanka) — z hotspotów Town (W9-A). */
export const MISIA_WAYPOINTS: readonly NpcWaypoint[] = [
  waypointAt('Kawiarnia', -24, 24),
  waypointAt('Szkoła', 20, 30),
  waypointAt('Domek', 16, 32),
  waypointAt('Basen', -12, 28),
  waypointAt('Park', 18, 20),
  waypointAt('Lodziarnia', -20, 26),
];

/** Cykl miejsc Loli (corgi) — lekko przesunięty względem Misi. */
export const LOLA_WAYPOINTS: readonly NpcWaypoint[] = [
  waypointAt('Sklep', 22, 30),
  waypointAt('Park', -28, 24),
  waypointAt('Stajnia', 14, 22),
  waypointAt('Domek', -18, 36),
  waypointAt('Kawiarnia', 26, 20),
  waypointAt('Plac zabaw', 12, 18),
];

/** Czas postoju na waypointcie (s) zanim idzie dalej. */
export const NPC_DWELL_SEC = 18;
/** Czas marszu między punktami (s) — cel animacji, nie fizyka. */
export const NPC_TRAVEL_SEC = 12;

export type NpcScheduleSample = {
  /** Indeks bieżącego celu. */
  waypointIndex: number;
  /** Pozycja interpolowana. */
  x: number;
  y: number;
  /** true gdy w drodze (nie stoi). */
  traveling: boolean;
  placePl: string;
};

/**
 * Pozycja NPC jako funkcja czasu sesji (sekundy od startu sceny).
 * Deterministyczna, bez RNG — testowalna.
 */
export function sampleNpcSchedule(
  sessionSec: number,
  waypoints: readonly NpcWaypoint[],
  opts: { dwellSec?: number; travelSec?: number; phaseOffsetSec?: number } = {},
): NpcScheduleSample {
  const dwell = opts.dwellSec ?? NPC_DWELL_SEC;
  const travel = opts.travelSec ?? NPC_TRAVEL_SEC;
  const n = waypoints.length;
  if (n === 0) {
    return { waypointIndex: 0, x: 0, y: 0, traveling: false, placePl: '' };
  }
  if (n === 1) {
    const w = waypoints[0]!;
    return {
      waypointIndex: 0,
      x: w.x,
      y: w.y,
      traveling: false,
      placePl: w.placePl,
    };
  }

  const t = Math.max(0, sessionSec + (opts.phaseOffsetSec ?? 0));
  const cycle = dwell + travel;
  const total = cycle * n;
  const u = t % total;
  const step = Math.floor(u / cycle) % n;
  const local = u - step * cycle;
  const from = waypoints[step]!;
  const to = waypoints[(step + 1) % n]!;

  if (local < dwell) {
    return {
      waypointIndex: step,
      x: from.x,
      y: from.y,
      traveling: false,
      placePl: from.placePl,
    };
  }

  const p = Math.min(1, (local - dwell) / travel);
  // smoothstep
  const s = p * p * (3 - 2 * p);
  return {
    waypointIndex: (step + 1) % n,
    x: from.x + (to.x - from.x) * s,
    y: from.y + (to.y - from.y) * s,
    traveling: true,
    placePl: to.placePl,
  };
}
